// shared-lib/cabinet-runs/deleteRun.js
//
// Deleting a run deletes EVERYTHING the run relates to (user ruling
// 2026-09-06): "if a run is deleted, everything should be deleted, inc uppers
// and appliances and the little fillers, surrounds, etc."
//
// A delete is NOT a re-solve, so it does not go through `mergeResolvedRuns`
// and it asks no policy. The policy says what a run REBUILDS — which is why a
// scan run's measured uppers and every section of an oven column (base,
// surround, over-oven cabinet alike) survive a sweep, so the next solve does
// not throw away geometry nothing would put back. A deleted run solves
// nothing ever again, so there is nothing for those exemptions to protect:
// they left the room holding a floating band of uppers over a stretch of
// bare floor, still tagged with a run that no longer exists.
//
// Four classes go, and each is a different way of BELONGING to the run —
// membership is not one field:
//
//  1. TAGGED — `runId` the run answers to (its own id, and the per-wall
//     aliases of a derived run joined at a corner). Lowers, countertops,
//     fillers, corner units, uppers (measured ones included), every section
//     of an oven column (base, surround, over-oven cabinet alike), a
//     re-seated tall unit, install pieces the run rebuilt.
//  2. NAMED BY A BAY — the appliance objects the run's bays claim
//     (`applianceObjectIds`) and anything stamped with one of its `bayId`s.
//     A scan/auto run POSITIONS these without owning them, so they carry no
//     `runId` and class 1 never sees them.
//  3. THE GROUP OF (2) — a sink's basin, faucet and host travel as one
//     `fixtureGroupId`; deleting the basin and leaving a faucet in mid-air is
//     not a delete.
//  4. THE INSTALL OF (2) — the fridge surround, the over-fridge cabinet, the
//     range hood (`isInstallPieceOf`): cabinetry that exists only because that
//     appliance stands there.
//  5. THE LEGACY SLAB — an UNTAGGED countertop whose cabinet parents all
//     belong to the run (`countertopRunId`). The generator emits its slabs
//     without a `runId`, and the merge's `keepRule` has always swept them by
//     asking whose cabinets they sit on; without the same rule a delete leaves
//     a slab floating over demolished cabinets.
//
// Survivors are then re-parented (`reparentOntoCountertops`), exactly as the
// merge does: a sink still naming a slab this delete took is drawn by NEITHER
// the slab path nor the plain path, so it vanishes.
//
// Pure and environment-free. Never mutates: untouched keepers come back by
// IDENTITY, in room order, so a caller can compare with `===`.
'use strict';

const { baysForRun } = require('./bays');
const { applianceObjectIds, isInstallPieceOf } = require('./applianceInstall');
const { runAnswersTo } = require('./runIds');
const { reparentOntoCountertops } = require('./reparent');
const { countertopRunId } = require('../kitchen-assembly/cabinetLayout/toRuns');

/**
 * The room's objects after `run` is deleted.
 *
 * `run` is the STORED run out of `room.cabinetRuns`, found by id or alias
 * (`findRunByIdOrAlias`). It THROWS without one rather than deleting on a bare
 * id: an id alone cannot name the bays, so classes 2-4 would silently do
 * nothing and the delete would leave the appliances behind.
 *
 * @param {Object[]} objects the room's objects
 * @param {Object} run the stored run being deleted
 * @returns {{objects: Object[], removedIds: string[]}}
 */
function objectsAfterRunDelete(objects, run) {
  if (!run || typeof run !== 'object') {
    throw new Error('objectsAfterRunDelete: no stored run to delete');
  }
  const list = Array.isArray(objects) ? objects : [];

  // (2) is computed first: it is the SUBJECT of (3) and (4), and an appliance
  // may also be tagged (a re-seated tall unit carries both), so membership is
  // read independently rather than as a chain of else-branches.
  const applianceIds = applianceObjectIds(run);
  const bayIds = new Set(
    baysForRun(run)
      .map((b) => b?.id)
      .filter(Boolean)
  );
  // Deliberately NOT `bayMatchesObject`, which is the RESOLVE-side matcher.
  // It also reads a bay's id as an object id, which is only true of an
  // appliance bay (`bay.id`/`applianceId` IS the appliance object's id on a
  // derived run) — so the two channels are split here: the `o.id` channel
  // asks only the APPLIANCE bays, while the `bayId` STAMP channel asks every
  // bay, because a filler or cabinet bay stamps objects it never names.
  const namedByBay = (o) =>
    (!!o?.id && applianceIds.has(o.id)) || (!!o?.bayId && bayIds.has(o.bayId));

  const appliances = list.filter(namedByBay);
  const groups = new Set(
    appliances.map((o) => o?.fixtureGroupId).filter(Boolean)
  );

  // Against the ORIGINAL objects: `countertopRunId` reads each parent's own
  // `runId`, so the map must be the room as it stands before the sweep.
  const byId = new Map(list.filter((o) => o?.id).map((o) => [o.id, o]));
  const legacySlabOfRun = (o) => {
    if (o?.runId || o?.type !== 'countertop') return false;
    const owner = countertopRunId(o, byId);
    return !!owner && runAnswersTo(run, owner);
  };

  const removes = (o) =>
    (!!o?.runId && runAnswersTo(run, o.runId)) || // 1 tagged
    namedByBay(o) || // 2 named by a bay
    (!!o?.fixtureGroupId && groups.has(o.fixtureGroupId)) || // 3 its group
    isInstallPieceOf(o, appliances) || // 4 its install
    legacySlabOfRun(o); // 5 its untagged slab

  const kept = [];
  const removedIds = [];
  for (const o of list) {
    if (!removes(o)) {
      kept.push(o);
      continue;
    }
    if (o?.id) removedIds.push(o.id);
  }
  return { objects: reparentOntoCountertops(kept), removedIds };
}

module.exports = { objectsAfterRunDelete };
